import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../api/axiosInstance';

// ── THUNKS ────────────────────────────────────────────────────────────────────
export const fetchShipments = createAsyncThunk(
  'shipments/fetchAll',
  async (params = {}, { rejectWithValue }) => {
    try {
      const { data } = await api.get('/shipments', { params });
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message);
    }
  }
);

export const fetchShipment = createAsyncThunk(
  'shipments/fetchOne',
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`/shipments/${id}`);
      return data.data.shipment;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message);
    }
  }
);

export const createShipment = createAsyncThunk(
  'shipments/create',
  async (formData, { rejectWithValue }) => {
    try {
      const { data } = await api.post('/shipments', formData);
      // `promo` (set only when a promoCode was submitted — see
      // shipmentController.createShipment) rides along separately from the
      // shipment itself, so callers can surface "Promo redeemed!" /
      // "Promo pending — confirmed once weighed in" without it polluting
      // the shipment object stored in state.
      return { shipment: data.data.shipment, promo: data.data.promo || null };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message);
    }
  }
);

export const updateShipment = createAsyncThunk(
  'shipments/update',
  async ({ id, updates }, { rejectWithValue }) => {
    try {
      const { data } = await api.patch(`/shipments/${id}`, updates);
      return data.data.shipment;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message);
    }
  }
);

export const updateShipmentStatus = createAsyncThunk(
  'shipments/updateStatus',
  async ({ id, status, note, notifyCustomer }, { rejectWithValue }) => {
    try {
      const { data } = await api.patch(`/shipments/${id}/status`, {
        status,
        note,
        notifyCustomer,
      });
      return data.data.shipment;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message);
    }
  }
);

export const syncShipmentTracking = createAsyncThunk(
  'shipments/syncTracking',
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await api.post(`/shipments/${id}/sync-tracking`);
      return data.data.shipment || { _id: id };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message);
    }
  }
);

export const deleteShipment = createAsyncThunk(
  'shipments/delete',
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`/shipments/${id}`);
      return id;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message);
    }
  }
);

// ── SLICE ─────────────────────────────────────────────────────────────────────
const shipmentSlice = createSlice({
  name: 'shipments',
  initialState: {
    list: [],
    current: null,
    pagination: { total: 0, page: 1, limit: 20, pages: 1 },
    loading: false,
    error: null,
  },
  reducers: {
    clearCurrent: (state) => { state.current = null; },
    clearError: (state) => { state.error = null; },
  },
  extraReducers: (builder) => {
    const setLoading = (state) => { state.loading = true; state.error = null; };
    const setError = (state, action) => { state.loading = false; state.error = action.payload; };

    builder
      .addCase(fetchShipments.pending, setLoading)
      .addCase(fetchShipments.fulfilled, (state, action) => {
        state.loading = false;
        state.list = action.payload.data;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchShipments.rejected, setError)

      .addCase(fetchShipment.pending, (state) => {
        // Clears `current` too, so navigating directly between two
        // different shipments never flashes the PREVIOUS one's full detail
        // page while the new one is still loading.
        state.current = null;
        setLoading(state);
      })
      .addCase(fetchShipment.fulfilled, (state, action) => {
        state.loading = false;
        state.current = action.payload;
      })
      .addCase(fetchShipment.rejected, (state, action) => {
        // Also clears `current` — without this, navigating straight from a
        // shipment the customer owns to one they don't (e.g. pasting a
        // different tracking number into the URL) left the PREVIOUS
        // shipment's data sitting in `current`, since a rejected fetch
        // otherwise never touches it — the page would then silently keep
        // showing the old shipment instead of surfacing the new error.
        state.current = null;
        setError(state, action);
      })

      .addCase(createShipment.fulfilled, (state, action) => {
        state.list.unshift(action.payload.shipment);
      })

      .addCase(updateShipment.fulfilled, (state, action) => {
        const idx = state.list.findIndex((s) => s._id === action.payload._id);
        if (idx !== -1) state.list[idx] = action.payload;
        if (state.current?._id === action.payload._id) state.current = action.payload;
      })

      .addCase(updateShipmentStatus.fulfilled, (state, action) => {
        const idx = state.list.findIndex((s) => s._id === action.payload._id);
        if (idx !== -1) state.list[idx] = action.payload;
        if (state.current?._id === action.payload._id) state.current = action.payload;
      })

      .addCase(syncShipmentTracking.pending, setLoading)
      .addCase(syncShipmentTracking.fulfilled, (state, action) => {
        state.loading = false;
        const idx = state.list.findIndex((s) => s._id === action.payload._id);
        if (idx !== -1) state.list[idx] = action.payload;
        if (state.current?._id === action.payload._id) state.current = action.payload;
      })
      .addCase(syncShipmentTracking.rejected, setError)

      .addCase(deleteShipment.fulfilled, (state, action) => {
        state.list = state.list.filter((s) => s._id !== action.payload);
      });
  },
});

export const { clearCurrent, clearError } = shipmentSlice.actions;

export const selectShipments = (state) => state.shipments.list;
export const selectCurrentShipment = (state) => state.shipments.current;
export const selectShipmentPagination = (state) => state.shipments.pagination;
export const selectShipmentsLoading = (state) => state.shipments.loading;
export const selectShipmentsError = (state) => state.shipments.error;

export default shipmentSlice.reducer;
